/* eslint-disable no-console */
import '../health-provider-translation-modal.scss';

import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  DeleteHealthProviderTranslationItemDocument,
  GetHealthProviderTranslationsDocument,
  HealthProviderItemDataFragment,
  HealthProviderTranslationItemDataFragment,
  UpdateHealthProviderTranslationItemDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { BlockType } from '../../../../elements/text-editor/elements/block-options-dropdown-list/block-options-dropdown-list';
import { TextEditor } from '../../../../elements/text-editor/text-editor';
import { useHealthProviderTranslationForm } from '../../form/use-health-provider-translation-form';

type HealthProviderTranslationModal = {
  healthProvider: HealthProviderItemDataFragment;
  healthProviderTranslationItemData: HealthProviderTranslationItemDataFragment;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

const blockTypes = [
  BlockType.PARAGRAPH,
  BlockType.BULLET_LIST,
  BlockType.NUMBER_LIST,
];

export function HealthProviderTranslationModal({
  healthProvider,
  healthProviderTranslationItemData,
  onSaved,
  onCancel,
  onClose,
}: HealthProviderTranslationModal) {
  const intl = useIntl();

  const { form, formIsValid, setErrors } = useHealthProviderTranslationForm();
  const { showAlertMessage } = useAppContext();

  const [deleteTranslationItem, { loading: deleteTranslationItemLoading }] =
    useMutation(DeleteHealthProviderTranslationItemDocument, {
      refetchQueries: [GetHealthProviderTranslationsDocument],
    });

  const [
    updateHealthProviderTranslationItem,
    { loading: updateHealthProviderTranslationItemLoading },
  ] = useMutation(UpdateHealthProviderTranslationItemDocument, {
    refetchQueries: [GetHealthProviderTranslationsDocument],
  });

  const reset = useCallback(async () => {
    await deleteTranslationItem({
      variables: {
        healthProviderId: healthProvider.id ?? '',
        key: healthProviderTranslationItemData?.key ?? '',
        type: healthProviderTranslationItemData?.type ?? '',
      },
    });
    form.setValue('textEn', '');
    form.setValue('textNl', '');
  }, [
    deleteTranslationItem,
    form,
    healthProvider.id,
    healthProviderTranslationItemData?.key,
    healthProviderTranslationItemData?.type,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const [resetPromptOpen, setResetPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      console.log(confirmed);

      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();
        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const onHandleReset = useCallback(async () => {
    await reset();
    onCancel?.();
    onClose?.();
  }, [onCancel, onClose, reset]);

  useEffect(() => {
    if (healthProviderTranslationItemData) {
      console.log('form reset');
      form.reset({
        key: healthProviderTranslationItemData.key ?? '',
        type: healthProviderTranslationItemData.type,
        textEn: healthProviderTranslationItemData.textEn ?? '',
        textNl: healthProviderTranslationItemData.textNl ?? '',
      });
    }
  }, [healthProviderTranslationItemData, form]);

  const currentTextNl = form.watch('textNl');
  const currentTextEn = form.watch('textEn');

  const canUpdate = currentTextNl !== '' || currentTextEn !== '';
  const canReset =
    healthProviderTranslationItemData?.textEn !== '' ||
    healthProviderTranslationItemData?.textNl !== '';

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      try {
        const result = await updateHealthProviderTranslationItem({
          variables: {
            ...values,
            healthProviderId: healthProvider.id ?? '',
          },
        });
        if (result.data?.healthProviderTranslationMutate?.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'health-provider-translation.health-provider-translation-update.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          setErrors(result.data?.healthProviderTranslationMutate?.errors);
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    healthProvider,
    intl,
    onClose,
    onSaved,
    setErrors,
    showAlertMessage,
    updateHealthProviderTranslationItem,
  ]);

  return (
    <>
      <Modal
        testId="health-provider-translation-modal"
        loading={false}
        title={healthProviderTranslationItemData?.key ?? ''}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              disabled={
                !canReset ||
                deleteTranslationItemLoading ||
                (currentTextEn ===
                  healthProviderTranslationItemData?.originalTextEn &&
                  currentTextNl ===
                    healthProviderTranslationItemData?.originalTextNl)
              }
              inverted
              onClick={() => {
                setResetPromptOpen(true);
              }}
            >
              <CustomFormattedMessage id="common.default-values" />
            </Button>

            <Button
              icon={
                updateHealthProviderTranslationItemLoading ||
                deleteTranslationItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.NONE
              }
              disabled={
                !canUpdate ||
                !formIsValid ||
                updateHealthProviderTranslationItemLoading ||
                deleteTranslationItemLoading
              }
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.change" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="common.practice" />
            </h4>
            {healthProvider?.name ?? ''}
          </div>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="health-provider-translation.type" />
            </h4>
            {healthProviderTranslationItemData?.type ?? ''}
          </div>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="health-provider-translation.info" />
            </h4>
            {healthProviderTranslationItemData?.info ?? ''}
          </div>

          <hr />
          <FormRow>
            <div className="description">
              <h4>
                <CustomFormattedMessage id="health-provider-translation.health-provider-translation-update.text-default-nl" />
              </h4>
              {healthProviderTranslationItemData?.originalTextNl ?? ''}
            </div>
            <h4>
              <CustomFormattedMessage id="health-provider-translation.health-provider-translation-update.text-override-nl" />
            </h4>
            <TextEditor
              blockTypes={blockTypes}
              html={healthProviderTranslationItemData?.textNl ?? ''}
              onChange={value => {
                console.log(value);
                form.setValue('textNl', value);
              }}
            />
          </FormRow>
          <hr />
          <FormRow>
            <div className="description">
              <h4>
                <CustomFormattedMessage id="health-provider-translation.health-provider-translation-update.text-default-en" />
              </h4>
              {healthProviderTranslationItemData?.originalTextEn ?? ''}
            </div>

            <TextEditor
              blockTypes={blockTypes}
              html={healthProviderTranslationItemData?.textEn ?? ''}
              onChange={value => {
                form.setValue('textEn', value);
              }}
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {resetPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleReset()}
          onClose={() => setResetPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-reset-changes" />
          </p>
        </ModalPrompt>
      )}

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
